/**
 * Admin -- Notifications/Delivery Log (Wave 1b W1b-27)
 *
 * Wired to /api/v1/admin/notifications/log. Mock data has been removed;
 * first paint pulls from the backend (50+ rows are seeded for the F61
 * admin org by the b43 migration so the page is never empty in dev).
 *
 * Filters: status (chip row), channel type (select), free-text search
 * across event_name + recipient + payload_summary. Retry button caps at
 * 3 attempts (422 if exceeded).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import {
  Loader2, Search, RefreshCw, Eye, AlertCircle, CheckCircle2,
  Clock, XCircle, Mail, MessageSquare, Webhook, Phone, Bell,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/lib/format';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';

type Status =
  | 'queued'
  | 'sending'
  | 'delivered'
  | 'failed'
  | 'suppressed'
  | 'retrying'
  | 'digested'
  | 'dead_lettered';

type ChannelType =
  | 'email'
  | 'slack'
  | 'pagerduty'
  | 'webhook'
  | 'sms'
  | 'in_app';

interface DeliveryRow {
  id: string;
  ruleId: string | null;
  ruleName: string | null;
  channelId: string | null;
  channelName: string | null;
  channelType: string | null;
  eventName: string;
  payloadSummary: string | null;
  recipient: string | null;
  status: Status;
  retryCount: number;
  errorMessage: string | null;
  queuedAt: string;
  deliveredAt: string | null;
}

interface DeliveryDetail extends DeliveryRow {
  eventPayload: Record<string, unknown>;
}

const STATUS_META: Record<
  Status,
  { label: string; color: string; icon: React.ComponentType<{ className?: string }> }
> = {
  queued:        { label: 'Queued',       color: 'border-slate-500/40 text-slate-700 dark:text-slate-300',     icon: Clock },
  sending:       { label: 'Sending',      color: 'border-blue-500/40 text-blue-700 dark:text-blue-300',       icon: RefreshCw },
  delivered:     { label: 'Delivered',    color: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-300', icon: CheckCircle2 },
  failed:        { label: 'Failed',       color: 'border-red-500/40 text-red-700 dark:text-red-300',           icon: XCircle },
  suppressed:    { label: 'Suppressed',   color: 'border-amber-500/40 text-amber-700 dark:text-amber-300',     icon: AlertCircle },
  retrying:      { label: 'Retrying',     color: 'border-orange-500/40 text-orange-700 dark:text-orange-300',  icon: RefreshCw },
  digested:      { label: 'Digested',     color: 'border-indigo-500/40 text-indigo-700 dark:text-indigo-300',  icon: AlertCircle },
  dead_lettered: { label: 'Dead-lettered', color: 'border-rose-500/40 text-rose-700 dark:text-rose-300',        icon: XCircle },
};

const ALL_STATUSES: Status[] = [
  'queued', 'sending', 'delivered', 'failed', 'suppressed', 'retrying', 'digested', 'dead_lettered',
];

const CHANNEL_ICON: Record<ChannelType, React.ComponentType<{ className?: string }>> = {
  email: Mail,
  slack: MessageSquare,
  pagerduty: AlertCircle,
  webhook: Webhook,
  sms: Phone,
  in_app: Bell,
};

const PAGE_SIZE = 25;

export const Route = createFileRoute('/admin/notifications/log')({
  component: LogPage,
});

function LogPage() {
  const [rows, setRows] = useState<DeliveryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const [channelTypeFilter, setChannelTypeFilter] = useState<ChannelType | ''>('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [retrying, setRetrying] = useState<string | null>(null);
  const [detail, setDetail] = useState<DeliveryDetail | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      if (statusFilter) params.set('status', statusFilter);
      if (channelTypeFilter) params.set('channelType', channelTypeFilter);
      if (search) params.set('q', search);

      const data = await apiClient.get<{
        deliveries: DeliveryRow[];
        page: number;
        pageSize: number;
        total: number;
      }>(`/admin/notifications/log?${params.toString()}`);
      setRows(data.deliveries);
      setTotal(data.total);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load deliveries';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, channelTypeFilter, search]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function retryDelivery(id: string) {
    try {
      setRetrying(id);
      await apiClient.post(`/admin/notifications/log/${id}/retry`, {});
      toast.success('Delivery requeued');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Retry failed';
      toast.error(msg);
    } finally {
      setRetrying(null);
    }
  }

  async function openDetail(id: string) {
    try {
      const d = await apiClient.get<DeliveryDetail>(`/admin/notifications/log/${id}`);
      setDetail(d);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load detail';
      toast.error(msg);
    }
  }

  function applySearch() {
    setSearch(searchInput.trim());
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Delivery Log</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Per-attempt history of every dispatched notification.
          Retry caps at 3 attempts. Investigate the underlying transport
          before retrying past the cap.
        </p>
      </div>

      {/* Filter bar */}
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-muted-foreground mr-1">Status:</span>
          <button
            type="button"
            onClick={() => { setStatusFilter(''); setPage(1); }}
            className={cn(
              'text-xs px-2 py-1 rounded-md border transition',
              !statusFilter
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background hover:bg-muted'
            )}
          >
            All
          </button>
          {ALL_STATUSES.map((s) => {
            const meta = STATUS_META[s];
            const active = statusFilter === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={cn(
                  'text-xs px-2 py-1 rounded-md border transition',
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : cn('bg-background hover:bg-muted', meta.color)
                )}
              >
                {meta.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Label className="text-xs text-muted-foreground">Channel type</Label>
          <Select
            value={channelTypeFilter || 'all'}
            onValueChange={(v) => {
              setChannelTypeFilter((v === 'all' ? '' : v) as ChannelType | '');
              setPage(1);
            }}
          >
            <SelectTrigger className="h-8 w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {(Object.keys(CHANNEL_ICON) as ChannelType[]).map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex-1 flex items-center gap-2 min-w-[260px]">
            <div className="relative flex-1">
              <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
                placeholder="Search event name, recipient, summary"
                className="h-8 pl-7 text-xs"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={applySearch}
            >
              Search
            </Button>
            {search && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs"
                onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
              >
                Clear
              </Button>
            )}
          </div>

          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() => refresh()}
            disabled={loading}
            title="Refresh the list"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-md">
        <div className="grid grid-cols-12 px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/40 border-b">
          <div className="col-span-3">Event / Summary</div>
          <div className="col-span-2">Channel</div>
          <div className="col-span-2">Recipient</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-1 text-center">Retries</div>
          <div className="col-span-2">Queued</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading deliveries…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-muted-foreground p-6 text-center italic">
            No deliveries match your filters.
          </div>
        ) : (
          <div className="divide-y">
            {rows.map((r) => (
              <DeliveryListRow
                key={r.id}
                row={r}
                onRetry={() => retryDelivery(r.id)}
                onView={() => openDetail(r.id)}
                retrying={retrying === r.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Showing {rows.length} of {total} deliveries
          {total > 0 && ` (page ${page} / ${totalPages})`}
        </span>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Detail sheet */}
      {detail && (
        <DeliveryDetailSheet
          row={detail}
          open
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function DeliveryListRow({
  row, onRetry, onView, retrying,
}: {
  row: DeliveryRow;
  onRetry: () => void;
  onView: () => void;
  retrying: boolean;
}) {
  const status = STATUS_META[row.status];
  const StatusIcon = status.icon;
  const ChIcon = row.channelType ? CHANNEL_ICON[row.channelType as ChannelType] : null;
  const canRetry =
    (row.status === 'failed' || row.status === 'retrying' || row.status === 'suppressed') &&
    row.retryCount < 3;

  return (
    <div className="grid grid-cols-12 px-3 py-2.5 items-center text-sm">
      <div className="col-span-3">
        <div className="font-mono text-[11px] truncate" title={row.eventName}>
          {row.eventName}
        </div>
        {row.payloadSummary && (
          <div className="text-xs text-muted-foreground truncate" title={row.payloadSummary}>
            {row.payloadSummary}
          </div>
        )}
        {row.ruleName && (
          <div className="text-[10px] text-muted-foreground italic">
            via {row.ruleName}
          </div>
        )}
      </div>
      <div className="col-span-2 flex items-center gap-1.5 text-xs">
        {ChIcon && <ChIcon className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="truncate" title={row.channelName ?? '(deleted)'}>
          {row.channelName ?? <span className="italic text-muted-foreground">(deleted)</span>}
        </span>
      </div>
      <div className="col-span-2 text-xs truncate" title={row.recipient ?? ''}>
        {row.recipient ?? <span className="italic text-muted-foreground">—</span>}
      </div>
      <div className="col-span-1 flex justify-center">
        <Badge variant="outline" className={cn('text-[10px] py-0 h-4 px-1.5 gap-1', status.color)}>
          <StatusIcon className="h-2.5 w-2.5" />
          {status.label}
        </Badge>
      </div>
      <div className="col-span-1 text-center text-xs">
        {row.retryCount > 0 ? (
          <span className={row.retryCount >= 3 ? 'text-red-500 font-medium' : ''}>
            {row.retryCount}/3
          </span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </div>
      <div className="col-span-2 text-xs">
        <div>{relativeTime(row.queuedAt)}</div>
        {row.errorMessage && (
          <div className="text-[10px] text-red-500 truncate" title={row.errorMessage}>
            {row.errorMessage}
          </div>
        )}
      </div>
      <div className="col-span-1 flex justify-end gap-1">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          title="View full payload"
          onClick={onView}
        >
          <Eye className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          title={
            canRetry
              ? 'Retry this delivery'
              : row.retryCount >= 3
                ? 'Retry cap reached'
                : 'Cannot retry from this status'
          }
          onClick={onRetry}
          disabled={!canRetry || retrying}
        >
          {retrying ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
    </div>
  );
}

function DeliveryDetailSheet({
  row, open, onClose,
}: {
  row: DeliveryDetail;
  open: boolean;
  onClose: () => void;
}) {
  const status = STATUS_META[row.status];
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Delivery detail</SheetTitle>
          <SheetDescription>
            <code className="font-mono text-[11px]">{row.id}</code>
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-5 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Status</Label>
              <div>
                <Badge variant="outline" className={cn('text-[10px] py-0 h-4 px-1.5', status.color)}>
                  {status.label}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Retry count</Label>
              <div className="text-sm">{row.retryCount} / 3</div>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Queued</Label>
              <div className="text-xs">{new Date(row.queuedAt).toLocaleString()}</div>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Delivered</Label>
              <div className="text-xs">
                {row.deliveredAt
                  ? new Date(row.deliveredAt).toLocaleString()
                  : <span className="italic text-muted-foreground">—</span>}
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground">Event</Label>
            <code className="block font-mono text-xs px-2 py-1.5 rounded bg-muted">
              {row.eventName}
            </code>
            {row.payloadSummary && (
              <p className="text-xs">{row.payloadSummary}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Rule</Label>
              <div className="text-xs">
                {row.ruleName ?? <span className="italic text-muted-foreground">(orphan / deleted)</span>}
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Channel</Label>
              <div className="text-xs">
                {row.channelName ?? <span className="italic text-muted-foreground">(deleted)</span>}
                {row.channelType && (
                  <span className="ml-1 text-muted-foreground">({row.channelType})</span>
                )}
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] text-muted-foreground">Recipient</Label>
              <div className="text-xs">
                {row.recipient ?? <span className="italic text-muted-foreground">—</span>}
              </div>
            </div>
          </div>

          {row.errorMessage && (
            <div className="space-y-1.5">
              <Label className="text-[10px] text-red-500">Error</Label>
              <div className="text-xs text-red-500 px-2 py-1.5 rounded bg-red-500/10 border border-red-500/30">
                {row.errorMessage}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground">Event payload</Label>
            <pre className="font-mono text-[11px] px-2 py-1.5 rounded bg-muted overflow-x-auto max-h-72">
              {JSON.stringify(row.eventPayload, null, 2)}
            </pre>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
